import React, { useEffect, useRef, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import PortalLayout from "../components/PortalLayout";
import { useAdminAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Upload, Check, Loader2, Trash2 } from "lucide-react";
import AdminSubNav from "../components/AdminSubNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function withVersion(url, v) {
    if (!url) return url;
    if (url.startsWith("http")) return url;
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}v=${v || 0}`;
}

function AngelUploadCard({ angel, onUploaded }) {
    const inputRef = useRef(null);
    const sketchInputRef = useRef(null);
    const [dragOver, setDragOver] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [sketchBusy, setSketchBusy] = useState(false);
    const [previewDesign, setPreviewDesign] = useState(null);

    const uploadSketch = useCallback(async (file) => {
        if (!file) return;
        setSketchBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(`/admin/angels/${angel.number}/sketch`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`Angel ${angel.number} — original sketch replaced.`);
            onUploaded(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSketchBusy(false);
            if (sketchInputRef.current) sketchInputRef.current.value = "";
        }
    }, [angel.number, onUploaded]);

    const removeSketch = useCallback(async () => {
        if (!window.confirm(`Remove the original sketch for Angel ${angel.number}? The "Original sketch" view disappears until you upload a new one.`)) return;
        setSketchBusy(true);
        try {
            const { data } = await api.delete(`/admin/angels/${angel.number}/sketch`);
            toast.success(`Angel ${angel.number} — sketch removed.`);
            onUploaded(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSketchBusy(false);
        }
    }, [angel.number, onUploaded]);

    const deleteDesign = useCallback(async (design) => {
        if (!window.confirm(`Delete ${design.label} from Angel ${angel.number}? Members who favourited it will fall back to the newest design.`)) return;
        setDeletingId(design.id);
        try {
            const { data } = await api.delete(`/admin/angels/${angel.number}/designs/${design.id}`);
            toast.success(`${design.label} deleted from Angel ${angel.number}.`);
            onUploaded(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setDeletingId(null);
        }
    }, [angel.number, onUploaded]);

    const upload = useCallback(async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Please choose an image file.");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(`/admin/angels/${angel.number}/image`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`Angel ${angel.number} updated.`);
            onUploaded(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setUploading(false);
        }
    }, [angel.number, onUploaded]);

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        upload(file);
    };

    const imgSrc = angel.mainImageUrl.startsWith("http")
        ? angel.mainImageUrl
        : `${BACKEND_URL}${withVersion(angel.mainImageUrl, angel.imageVersion)}`;

    return (
        <div
            className={`relative border ${
                dragOver ? "border-[#cfa052]" : "border-white/10"
            } bg-[#151311] overflow-hidden rounded-sm transition-colors`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            data-testid={`admin-card-${angel.number}`}
        >
            <div className="aspect-[4/5] relative bg-[#0c0b0a]">
                <img
                    src={imgSrc}
                    alt={`Angel ${angel.number}`}
                    className={`w-full h-full ${angel.hasArtwork ? "object-contain p-3" : "object-cover"}`}
                />
                <div className="absolute bottom-2 left-2 font-numerals text-xl text-[#cfa052] drop-shadow">
                    {angel.number}
                </div>
                {angel.hasArtwork && (
                    <div className="absolute top-2 right-2 bg-[#cfa052]/90 text-[#0c0b0a] text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm flex items-center gap-1">
                        <Check className="w-3 h-3" /> {angel.designs?.length > 1 ? `${angel.designs.length} designs` : "art"}
                    </div>
                )}
                {uploading && (
                    <div className="absolute inset-0 bg-[#0c0b0a]/80 flex items-center justify-center">
                        <Loader2 className="w-8 h-8 text-[#cfa052] animate-spin" />
                    </div>
                )}
                {previewDesign && (
                    <div
                        className="absolute inset-0 z-10 bg-[#0c0b0a]/95 flex flex-col items-center justify-center gap-2 pointer-events-none"
                        data-testid={`admin-design-preview-${angel.number}`}
                    >
                        <img
                            src={previewDesign.url.startsWith("http")
                                ? previewDesign.url
                                : `${BACKEND_URL}${withVersion(previewDesign.url, previewDesign.version)}`}
                            alt={previewDesign.label}
                            className="max-w-[85%] max-h-[80%] object-contain"
                        />
                        <span className="font-label text-[9px] uppercase tracking-[0.2em] text-[#cfa052]">
                            {previewDesign.label}
                        </span>
                    </div>
                )}
            </div>

            <div className="px-3 py-3 border-t border-white/5 flex items-center justify-between gap-2">
                <span className="font-poetic text-xs text-[#6e6a62] truncate">
                    {angel.statusText}
                </span>
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    data-testid={`admin-upload-btn-${angel.number}`}
                    className="flex items-center gap-1.5 font-label text-[9px] text-[#cfa052] hover:text-[#e0b569] transition-colors"
                >
                    <Upload className="w-3 h-3" /> {angel.hasArtwork ? "Add design" : "Upload"}
                </button>
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid={`admin-file-input-${angel.number}`}
                    onChange={(e) => upload(e.target.files?.[0])}
                />
            </div>

            {angel.designs?.length > 0 && (
                <div className="px-3 pb-3 flex flex-wrap gap-1.5" data-testid={`admin-designs-${angel.number}`}>
                    {angel.designs.map((d) => (
                        <span
                            key={d.id}
                            onMouseEnter={() => setPreviewDesign(d)}
                            onMouseLeave={() => setPreviewDesign(null)}
                            onFocus={() => setPreviewDesign(d)}
                            onBlur={() => setPreviewDesign(null)}
                            tabIndex={0}
                            data-testid={`admin-design-chip-${angel.number}-${d.id}`}
                            className="inline-flex items-center gap-1 border border-white/10 bg-[#0c0b0a] text-[#9a958a] hover:text-[#cfa052] hover:border-[#cfa052]/40 text-[9px] font-label uppercase tracking-[0.15em] pl-2 pr-1 py-0.5 rounded-full cursor-help transition-colors"
                        >
                            {d.label.replace("Design ", "")}
                            <button
                                type="button"
                                onClick={() => deleteDesign(d)}
                                disabled={deletingId === d.id}
                                aria-label={`Delete ${d.label}`}
                                data-testid={`admin-delete-design-${angel.number}-${d.id}`}
                                className="p-0.5 text-[#6e6a62] hover:text-[#c96f4a] transition-colors disabled:opacity-40"
                            >
                                {deletingId === d.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                            </button>
                        </span>
                    ))}
                </div>
            )}
            {angel.hasArtwork && !(angel.designs?.length > 0) && (
                <div className="px-3 pb-2 font-label text-[9px] uppercase tracking-[0.15em] text-[#6e6a62]" data-testid={`admin-sketch-only-${angel.number}`}>
                    Sketch only — no designs yet
                </div>
            )}
            {angel.hasArtwork && (
                <div className="px-3 pb-3 flex items-center gap-2" data-testid={`admin-sketch-controls-${angel.number}`}>
                    <button
                        type="button"
                        onClick={() => sketchInputRef.current?.click()}
                        disabled={sketchBusy}
                        data-testid={`admin-replace-sketch-${angel.number}`}
                        className="inline-flex items-center gap-1 border border-white/10 bg-[#0c0b0a] text-[#9a958a] hover:text-[#cfa052] text-[9px] font-label uppercase tracking-[0.15em] px-2 py-1 rounded-full transition-colors disabled:opacity-40"
                    >
                        {sketchBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                        {angel.placeholderImageUrl ? "Replace sketch" : "Add sketch"}
                    </button>
                    {angel.placeholderImageUrl?.includes("-placeholder.jpg")
                        && angel.mainImageUrl !== angel.placeholderImageUrl && (
                        <button
                            type="button"
                            onClick={removeSketch}
                            disabled={sketchBusy}
                            aria-label={`Remove sketch for Angel ${angel.number}`}
                            data-testid={`admin-remove-sketch-${angel.number}`}
                            className="p-1 border border-white/10 bg-[#0c0b0a] rounded-full text-[#6e6a62] hover:text-[#c96f4a] transition-colors disabled:opacity-40"
                        >
                            <Trash2 className="w-3 h-3" />
                        </button>
                    )}
                    <input
                        ref={sketchInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        data-testid={`admin-sketch-input-${angel.number}`}
                        onChange={(e) => uploadSketch(e.target.files?.[0])}
                    />
                </div>
            )}
        </div>
    );
}

export default function Admin() {
    const { admin } = useAdminAuth();
    const [angels, setAngels] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                const { data } = await api.get("/angels");
                setAngels(data);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    if (admin === null) return null;
    if (!admin) return <Navigate to="/login" replace />;

    const handleUploaded = (updated) => {
        setAngels((prev) => prev.map((a) => (a.number === updated.number ? updated : a)));
    };

    const artworkCount = angels.filter((a) => a.hasArtwork).length;

    return (
        <PortalLayout>
            <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-16 pb-32" data-testid="admin-page">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
                    <div>
                        <span className="font-label text-[11px] text-[#cfa052]">Admin · curator</span>
                        <h1 className="mt-4 font-serif-display text-5xl md:text-6xl tracking-tighter text-[#e8e4db]" data-testid="admin-title">
                            Replace Angel <span className="italic">artwork</span>
                        </h1>
                        <p className="mt-4 font-poetic text-[#9a958a] max-w-xl">
                            Drag an image onto any card — or click <span className="text-[#cfa052]">Replace</span>. The portrait is auto-padded to 4:5 on a warm-black canvas.
                        </p>
                    </div>
                    <div className="text-right font-poetic text-[#9a958a] text-sm">
                        <p>{artworkCount} / 60 with artwork</p>
                        <p className="text-[#6e6a62] text-xs mt-1">Changes are live immediately for all members.</p>
                    </div>
                </div>

                <AdminSubNav />

                {loading ? (
                    <div className="text-center py-32 font-poetic text-[#9a958a]" data-testid="admin-loading">loading angels…</div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6" data-testid="admin-grid">
                        {angels.map((angel) => (
                            <AngelUploadCard key={angel.number} angel={angel} onUploaded={handleUploaded} />
                        ))}
                    </div>
                )}
            </section>
        </PortalLayout>
    );
}
